import type { BootstrapData, PublicConfig } from "../domain/app-types.ts";

export interface ApplicationStartupCapabilities {
  registerServiceWorker(): void;
  requestPersistentStorage(): void;
  bindGlobalEvents(): void;
  request(path: string, init?: RequestInit): Promise<Response>;
  setPublicConfig(config: PublicConfig | null): void;
  cacheBootstrap(body: BootstrapData): Promise<void>;
  markSignedIn(): Promise<void>;
  restoreBootstrap(body: BootstrapData): Promise<void>;
  refreshInvitations(): Promise<void>;
  refreshCalendarConnections(): Promise<void>;
  search(): string;
  clearUrl(): void;
  reconcileCalendars(): void;
  refreshAccess(): Promise<void>;
  apiError(response: Response, fallback: string): Promise<string>;
  toast(message: string, error?: boolean): void;
  renderShell(): void | Promise<void>;
  refreshNotifications(): void;
  sync(): Promise<void>;
  taskExists(id: string): boolean;
  openTask(id: string): Promise<void>;
  openCollectionNote?(id: string, section?: string): Promise<void>;
  openBrowserCapture?(): Promise<boolean>;
  openIntake?(): Promise<boolean>;
  focusComment(id: string): void;
  signedOut(): Promise<boolean>;
  cachedBootstrap(): Promise<BootstrapData | null>;
  setOffline(): void;
  renderAuth(message?: string): void;
}

export async function startApplication(capabilities: ApplicationStartupCapabilities): Promise<void> {
  capabilities.registerServiceWorker();
  capabilities.requestPersistentStorage();
  capabilities.bindGlobalEvents();
  const configRequest = capabilities.request("/api/v1/config").then(async response => response.ok ? await response.json() as PublicConfig : null).catch(() => null);
  const response = await capabilities.request("/api/v1/bootstrap", { credentials: "include" }).catch(() => null);
  capabilities.setPublicConfig(await configRequest);
  if (response?.ok) {
    const body = await response.json() as BootstrapData;
    await capabilities.cacheBootstrap(body);
    await capabilities.markSignedIn();
    await capabilities.restoreBootstrap(body);
    await capabilities.refreshInvitations();
    await capabilities.refreshCalendarConnections();
    const calendarResult = new URLSearchParams(capabilities.search()).get("calendar");
    if (calendarResult) {
      capabilities.clearUrl();
      if (calendarResult === "connected") capabilities.reconcileCalendars();
    }
    const inviteToken = new URLSearchParams(capabilities.search()).get("invite");
    if (inviteToken) {
      const accepted = await capabilities.request(`/api/v1/invitations/token/${encodeURIComponent(inviteToken)}`, { method: "POST", credentials: "include" });
      if (accepted.ok) { capabilities.clearUrl(); await capabilities.refreshAccess(); }
      else capabilities.toast(await capabilities.apiError(accepted, "This invitation is unavailable"), true);
    }
    const linkedParameters = new URLSearchParams(capabilities.search());
    const linkedTask = linkedParameters.get("task");
    const linkedComment = linkedParameters.get("comment");
    const linkedCollection = linkedParameters.get("collection");
    const linkedNote = linkedParameters.get("note");
    const shellReady = Promise.resolve(capabilities.renderShell());
    capabilities.refreshNotifications();
    void (async () => {
      await shellReady;
      // Review capture immediately after authentication, without waiting for network sync.
      const captured = await capabilities.openBrowserCapture?.() || await capabilities.openIntake?.();
      await capabilities.sync();
      if (captured) return;
      if (linkedTask && capabilities.taskExists(linkedTask)) {
        capabilities.clearUrl();
        await capabilities.openTask(linkedTask);
        if (linkedComment) capabilities.focusComment(linkedComment);
      } else if (linkedCollection && linkedNote && capabilities.openCollectionNote) {
        capabilities.clearUrl();
        await capabilities.openCollectionNote(linkedCollection, linkedNote);
      }
    })();
    return;
  }
  const signedOut = await capabilities.signedOut();
  const cached = await capabilities.cachedBootstrap();
  if (!response && !signedOut && cached) {
    capabilities.setOffline();
    await capabilities.restoreBootstrap(cached);
    await capabilities.renderShell();
    if (await capabilities.openBrowserCapture?.() || await capabilities.openIntake?.()) return;
    const linkedParameters = new URLSearchParams(capabilities.search());
    const linkedCollection = linkedParameters.get("collection");
    const linkedNote = linkedParameters.get("note");
    if (linkedCollection && linkedNote && capabilities.openCollectionNote) {
      capabilities.clearUrl();
      await capabilities.openCollectionNote(linkedCollection, linkedNote);
    }
  } else {
    capabilities.renderAuth(response && response.status !== 401 ? "The server is unavailable. Try again shortly." : "");
  }
}
